/**
 * `ensureStorefrontSession` -- the ONE step that establishes a storefront
 * session for a store, extracted from StorefrontSessionGate.
 *
 * Plan: docs/planning/2026-10-02-storefront-session-everywhere-plan.md
 *
 * StorefrontSessionGate calls it on entry to any shopper page; nothing else
 * creates storefront sessions (the auth interceptor's mid-visit recovery
 * shares the mint itself, router/anonymousStorefrontMint.js). On success the
 * browser holds BOTH halves of the session -- the token and the store id
 * (`campaignId`) -- in memory AND in storage (writes guarded). Before this,
 * only the gate wrote the store id and the cart page sat outside the gate, so
 * a phone holding a token but not the store id rendered a blank cart (AH, prod
 * 2026-10-02).
 *
 * THE STORE ID IS RESOLVED FROM THE URL SLUG, never assumed from storage:
 * storage only ever answers "is the session I hold for this store?".
 *
 * Steps, in order:
 *   1. slug -> store id (404: the store is not there; no id: the store
 *      cannot be opened, see 4);
 *   2. preview / privileged sessions: no token swap (see below);
 *   3. already holding this store's session in Vuex: keep it;
 *   4. the store must be active. A store that cannot be opened is asked
 *      about once more: the backend reports a store that launched and was
 *      closed again as CLOSED (card #335), anything else as not there;
 *   5. a HELD session for this store (or one whose store id was lost): keep
 *      it if it still works; a dead ANONYMOUS one is replaced;
 *   6. a fragment RESUME CODE, when there is no usable session: redeem it for
 *      the same anonymous account (router/storefrontResumeCode.js) -- allowed
 *      after the store's close, so a reload mid-checkout keeps its cart. A
 *      redeem that fails for ANY reason drops the code and falls through;
 *   7. otherwise mint a fresh anonymous session (a new visitor to a CLOSED
 *      store is refused here), joining a session another tab just minted.
 *
 * THE EMAILED ORDER LINK (`orders/:id?token=`, see `isEmailedOrderLink`) is
 * never refused (P1-2 on #3983): its one-order code is the credential and the
 * guest read needs no session. An OPEN store is entered as usual -- the
 * store's branding read (getStorefrontCampaign) needs a session, and without
 * one its 401 ends the visit on the store home. A store that is closed,
 * inactive, or whose slug no longer resolves (deactivated) renders the order
 * with NO session: `{status: "ready", sessionless: true}`.
 *
 * THE ORDER PAGES (`orders`, `orders/:id`; `ownOrdersSession.isOrderPage`)
 * get the same treatment (Jake, OD-20261016-3 follow-up, 2026-10-16): a
 * shopper of a closed or archived store keeps them. Rendered with no store
 * session, the page shows a signed-in shopper their own orders and anyone
 * else the order-link form (router/ownOrdersSession.js). Every OTHER page of
 * such a store is refused exactly as before.
 */
import { describeError } from "@/utils/safeErrorLog";
import { isPrivilegedSession } from "@/router/privilegedSession";
import {
  AccountNotForStore,
  ResumeCodeRefused,
  mintRequestConfig,
  mintAnonymousStorefrontSession,
  redeemStorefrontResumeCode,
  adoptStorefrontAccount,
} from "@/router/anonymousStorefrontMint";
import { isAnonymousStorefrontSessionNow } from "@/router/sessionProvenance";
import { SKIP_AUTH_RECOVERY } from "@/interceptors/authResponseInterceptor";
import { holdStorefrontStoreId } from "@/router/storefrontStoreId";
import {
  holdResumeCode,
  forgetResumeCode,
  dropResumeFragment,
} from "@/router/storefrontResumeCode";
import { getActivePinia } from "pinia";
import { useAccountStore } from "@/stores/account";

// "This store is not there" -- thrown ONLY by the steps that ask about the
// store itself: the slug lookup and the token door refusing the store. The
// gate shows "Store not found" for this and nothing else. A 404 from any OTHER
// step (an account fetch, for one) says nothing about the store (gate
// bar-raiser P1-2 on #3957).
export class StoreNotFound extends Error {
  constructor(step) {
    super(`store not found at ${step}`);
    this.name = "StoreNotFound";
  }
}

// "This store exists and is CLOSED to new shoppers" -- a token door's 410
// `code: "store_closed"` (STORE_CLOSED_CODE in api/src/endpoints/auth.py) or
// the backend's closed lookup (`getIsStoreClosed`): a Pop-Up past its close,
// or a store that launched and was closed again (card #335). `notice` is the
// lookup's answer (`closedNoticeFrom`), or undefined when the refusal came
// without one; the closed screen then looks it up.
export class StoreClosed extends Error {
  constructor(notice) {
    super("store closed");
    this.name = "StoreClosed";
    this.notice = notice;
  }
}

/**
 * The closed notice in a `getIsStoreClosed` body: `{storeName, logoKey,
 * contactEmail}`, each null when absent. The body carries nothing else about
 * the store (backend `_closed_notice`).
 */
export function closedNoticeFrom(data) {
  const text = (v) => (typeof v === "string" && v.trim() !== "" ? v : null);
  return {
    storeName: text(data?.store_name),
    logoKey: text(data?.logo_key),
    contactEmail: text(data?.contact_email),
  };
}

const isStatus = (err, status) => err?.response?.status === status;

/**
 * Is this route the emailed order link -- order detail carrying its one-order
 * code (`?token=`)? Such a visit establishes NO session: the order page reads
 * through the guest endpoint (orderDetailHelpers.guestOrderRequest), and a
 * store that has since closed or been deactivated does not revoke the code.
 */
export function isEmailedOrderLink(route) {
  const token = route?.query?.token;
  return (
    route?.name === "storefront_order_detail" &&
    typeof token === "string" &&
    token.trim() !== ""
  );
}

const isStoreClosed = (err) =>
  isStatus(err, 410) && err?.response?.data?.code === "store_closed";

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Unusable storage: memory carries the session for this page.
  }
}

/**
 * Establish the storefront session for the store `slug` names.
 *
 * @param {object} deps
 * @param {string} deps.slug           The store slug, from the URL.
 * @param {object} deps.axiosInstance  The app axios instance.
 * @param {object} deps.store          The Vuex store.
 * @param {boolean} [deps.preview]     The store-editor preview iframe.
 * @param {string|null} [deps.resumeCode] A `#r=` code from the URL fragment.
 * @param {boolean} [deps.sessionlessOk] The route may render with no store
 *   session -- the emailed order link or an order page: never refused, see
 *   the header.
 * @returns {Promise<{status: "ready", campaignId, sessionless?: true} | {status: "invalid"}>}
 * @throws StoreNotFound | StoreClosed | the underlying error (retryable).
 */
export async function ensureStorefrontSession({
  slug,
  axiosInstance,
  store,
  preview = false,
  resumeCode = null,
  sessionlessOk = false,
}) {
  const idResp = await axiosInstance
    .get(`/campaign/getCampaignIdFromShopperURL/${slug}`, mintRequestConfig())
    .catch((err) => {
      throw isStatus(err, 404) ? new StoreNotFound("slug lookup") : err;
    });
  const campaignId = idResp?.data ?? null;
  // Where any other page is refused, the emailed order link and the order
  // pages render with no session.
  const sessionless = { status: "ready", campaignId, sessionless: true };
  if (!campaignId) return sessionlessOk ? sessionless : notOpenable({ slug, axiosInstance });

  // Keep the legacy slug fallback chain working: storeUrl is resolution step
  // 3 in resolveStorefrontSlug and the confirmation page's continue-shopping
  // link.
  writeStorage("storeUrl", slug);

  // NEITHER preview NOR a privileged session is swapped to a Shopper token.
  // They need the same swap refusal but DIFFERENT amounts of state.
  //
  // PREVIEW: the store-editor embeds the storefront in a same-origin iframe
  // (?preview=1) that SHARES localStorage AND the Vuex store with the parent
  // editor. The shopper token swap would clobber the editor's (e.g.
  // superadmin impersonation) session and downgrade its API calls to Shopper
  // (500/403) -- and for the same reason even the read-only hydration in the
  // privileged branch below would mutate the EDITOR's store. So preview
  // touches NO shared state at all. See router/storefrontPreview.js.
  if (preview) return { status: "ready", campaignId };

  // PRIVILEGED (and not preview) -- a back-office user (any non-Shopper role)
  // or an active impersonation, in their own tab. Still NO token swap: it
  // would tear down their session (own-store preview, or the storefront open
  // in a second same-origin tab that shares localStorage). Only a genuine
  // Shopper or an anonymous visitor is swapped. See router/privilegedSession.js.
  //
  // Refusing the swap leaves `state.account` null, and the topbar badge and
  // the cart page read the cart of `state.account` -- so the actor's own
  // account is hydrated instead, giving this session its own cart identity.
  if (isPrivilegedSession()) {
    await hydratePrivilegedAccount(store);
    return { status: "ready", campaignId };
  }

  // Fast path: already inside this store's shopper session -- no token swap,
  // no login round-trip.
  const account = store.state.account;
  if (
    store.getters.isLoggedIn &&
    store.getters.isShopper &&
    account &&
    String(account.campaign_id) === String(campaignId)
  ) {
    holdStoreId(slug, campaignId);
    return { status: "ready", campaignId };
  }

  const activeResp = await axiosInstance.get(
    `/campaign/getIsCampaignActive/${campaignId}`,
    mintRequestConfig()
  );
  if (!activeResp?.data) {
    return sessionlessOk ? sessionless : notOpenable({ slug, axiosInstance });
  }

  // NOTE: the shared-password wall is NOT decided here. It is the router
  // guard router/storefrontPasswordGate.js, which covers the whole
  // /shop/:slug/* tree by path. Clearing the wall does not swap the visitor
  // onto a shared account either: a cleared visitor stays anonymous and
  // identity is collected at checkout.
  try {
    await loginShopper({ slug, axiosInstance, store, campaignId, resumeCode });
  } catch (err) {
    if (sessionlessOk && err instanceof StoreClosed) return sessionless;
    throw err;
  }
  holdStoreId(slug, campaignId);
  return { status: "ready", campaignId };
}

/**
 * A store this visitor cannot open: closed (throws `StoreClosed`) or, as far
 * as the public can tell, not there (`{status: "invalid"}`).
 *
 * Card #335 (Jake, 2026-10-24): a store that LAUNCHED and was closed again
 * shows the closed notice; one that never launched stays "Store not found".
 * The backend's closed lookup decides -- it answers a never-launched store
 * exactly as a missing one -- so nothing here can tell them apart.
 */
async function notOpenable({ slug, axiosInstance }) {
  await refuseIfClosed({ slug, axiosInstance });
  return { status: "invalid" };
}

/** Both halves of the store id: memory and storage. */
function holdStoreId(slug, campaignId) {
  holdStorefrontStoreId(slug, campaignId);
  writeStorage("campaignId", campaignId);
}

/**
 * Populate Vuex `state.account` for a PRIVILEGED session from the ACTOR's own
 * account row -- no token swap, no shopper session.
 *
 * `setAccount` ALONE, deliberately NOT `dispatch('login')`: `login` also
 * writes `campaignId`, `distributorId`, `roleId` and `roleName` to
 * localStorage, and `roleName` is the exact signal `isPrivilegedSession()`
 * reads -- writing it from here risks re-opening the cross-tab logout bug the
 * no-swap rule exists to prevent. The tenant keys are left alone for a second
 * reason: this session's effective tenant is the IMPERSONATED distributor
 * (`stores/effectiveTenant.js`).
 *
 * The id comes from `localStorage["userId"]`, which every login path writes
 * and which impersonation start/stop never rewrites -- the actor's own id even
 * mid-impersonation, and exactly the row `_assert_self_or_act_as` accepts as
 * "self" (resolves at all only because of #2997).
 *
 * Failure degrades to read-only, never worse: it must never blank a session
 * or bounce a user.
 */
async function hydratePrivilegedAccount(store) {
  const actorId = readStorage("userId");
  if (!actorId) return;
  try {
    const actor = await store.dispatch("jv/get", [
      `accounts/${actorId}`,
      { params: { include: "role" } },
    ]);
    if (actor) store.commit("setAccount", actor);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("Privileged storefront account hydration failed", describeError(err));
  }
}

async function loginShopper({ slug, axiosInstance, store, campaignId, resumeCode }) {
  const shopperResp = await axiosInstance.get(
    `/campaign/getShopperFromCampaignID/${campaignId}`,
    mintRequestConfig()
  );
  const shopperAccount = shopperResp?.data;

  // A store with NO template shopper is NOT an invalid store: the template is
  // only a way to NAME THE STORE to `/auth/getToken/<account_id>`, and a
  // template-less store uses `/auth/getStoreToken/<store_id>` (PR #3795),
  // which returns the same response shape. The template path stays PRIMARY.
  const templateAccountId =
    shopperAccount && shopperAccount.id != null ? shopperAccount.id : null;

  const token = readStorage("token");

  // THE QUESTION: does this browser already hold a valid session FOR THIS
  // STORE? If it does, leave it alone. Decided by STORE SCOPE, never by
  // comparing the held account against the store's template shopper (true for
  // exactly one account -- GH #3242). A held session is a held session,
  // anonymous or credentialed; the swap exists only to ESTABLISH one where
  // none is.
  //
  // `campaignId` in storage answers "which store is this session for?" --
  // `login` writes it for every session, `logout` removes it. A session for a
  // DIFFERENT store is swapped. A session whose store id the browser LOST
  // (WebKit dropped the key; prod 2026-10-02, Repro 2) is asked: its account
  // row names its store, so it is kept when that is this store and swapped
  // otherwise -- dropping it on sight would throw away the visitor's cart.
  //
  // Only when the session's OWN account id is known: the template fallback
  // below is for a session already known to be this store's, and asking the
  // template whether an unknown session is this store's would always say yes.
  const heldCampaignID = readStorage("campaignId");
  const heldUserId = readStorage("userId");
  const storeIdLost = (heldCampaignID == null || heldCampaignID === "") && !!heldUserId;
  const holdsSessionForThisStore =
    String(heldCampaignID) === String(campaignId) || storeIdLost;

  if (token && holdsSessionForThisStore) {
    // Hydrate the account this session ACTUALLY holds (the per-visitor row),
    // NOT the template -- the template is only the name of the store. It is
    // the fallback because a template-less store has no template to fall
    // back TO.
    const sessionAccountId = heldUserId || templateAccountId;
    const heldIsAnonymous = isAnonymousStorefrontSessionNow(token);
    try {
      await adoptStorefrontAccount({
        store,
        accountId: sessionAccountId,
        // An anonymous session's 401 is OURS to handle (re-mint below); left
        // to the interceptor it would race this step with a logout.
        requestConfig: heldIsAnonymous ? { [SKIP_AUTH_RECOVERY]: true } : undefined,
        expectCampaignId: storeIdLost ? campaignId : undefined,
      });
      // The returning visitor did not mint, so nothing else puts their bearer
      // in memory.
      //
      // A resume code in the URL is NOT adopted here: a usable session ignores
      // it, and the URL may be someone else's pasted cart link. Binding that
      // code to THIS visitor's account would later restore the wrong cart.
      // Their own code (held in storage, bound to their account) is what the
      // gate writes back into the address bar.
      if (heldIsAnonymous) holdAnonymousInMemory();
      return;
    } catch (err) {
      const status = err?.response?.status;
      // AccountNotForStore: a session whose lost store id turned out to be
      // another store's -- swapped like any other store's session.
      // 401: reaped/unknown token. 404: a valid token for someone else's row
      // (the row-scoping seam). Either way a held ANONYMOUS session is dead --
      // replace it. Anything else, or a credentialed session, is not ours to
      // paper over.
      const replaceable =
        err instanceof AccountNotForStore ||
        (heldIsAnonymous && (status === 401 || status === 404));
      if (!replaceable) throw err;
    }
  }

  // No usable session. A resume code in the URL brings back the SAME
  // anonymous account (and its cart) -- asked BEFORE minting, and before the
  // closed check: a shopper who already held a session keeps it after close.
  if (resumeCode && (await resumeWithCode({ slug, axiosInstance, store, campaignId, code: resumeCode }))) {
    return;
  }

  const minted = await mintForThisStore({ slug, axiosInstance, campaignId, templateAccountId });

  // The anonymous marker is set inside adoptStorefrontAccount, DERIVED FROM
  // THE FACT (the session's recorded provenance), read AFTER the `login`
  // dispatch -- not from "did this visit swap?" (GH #2941 defect 2).
  try {
    await adoptStorefrontAccount({
      store,
      accountId: minted.accountId,
      minted: minted.session,
    });
    holdMintedCode(slug, minted);
  } catch (err) {
    // TWO TABS minting at once share one localStorage: the later mint
    // overwrote this tab's token, so this tab's fetch of its own row went out
    // on the other tab's bearer and 404'd. Join the session storage now holds
    // rather than minting a third -- but ONLY an anonymous session whose
    // account belongs to THIS store (gate r2 P2-1 on #3957). Anything else:
    // mint a fresh session.
    const storedId = readStorage("userId");
    if (!isStatus(err, 404) || !storedId || storedId === String(minted.accountId)) {
      throw err;
    }
    if (await joinStoredAnonymousSession({ store, campaignId, storedId })) {
      holdAnonymousInMemory();
      // The joined session is the OTHER tab's; this tab's code names an
      // account nobody holds any more.
      forgetResumeCode();
    } else {
      const fresh = await mintForThisStore({ slug, axiosInstance, campaignId, templateAccountId });
      await adoptStorefrontAccount({
        store,
        accountId: fresh.accountId,
        minted: fresh.session,
      });
      holdMintedCode(slug, fresh);
    }
  }
}

function holdMintedCode(slug, minted) {
  if (minted.resumeCode) holdResumeCode(slug, minted.resumeCode, minted.accountId);
  else forgetResumeCode();
}

/**
 * Redeem a fragment resume code. Resolves true when the session is restored,
 * false otherwise -- and the caller then mints a fresh session.
 *
 * ANY failure is a refusal, not only a 404 or a wrong-store answer: a 429, a
 * 5xx, a timeout, a network error, or a failed fetch of the resumed account.
 * The code is forgotten and stripped from the URL, so a retry or a reload can
 * never re-send it. A resume code never puts a shopper on an error screen
 * (P1-1 on #3983): losing that cart beats a store that will not open.
 */
async function resumeWithCode({ slug, axiosInstance, store, campaignId, code }) {
  try {
    const resumed = await redeemStorefrontResumeCode({ axiosInstance, campaignId, code });
    await adoptStorefrontAccount({
      store,
      accountId: resumed.accountId,
      minted: resumed.session,
    });
    holdResumeCode(slug, code, resumed.accountId);
    return true;
  } catch (err) {
    if (!(err instanceof ResumeCodeRefused)) {
      // eslint-disable-next-line no-console
      console.warn("Storefront resume code failed; starting a fresh session", err?.message);
    }
    forgetResumeCode();
    dropResumeFragment();
    return false;
  }
}

/**
 * Mint at this store's token door; a 404 there means the store is not there.
 *
 * A NEW visitor is first asked whether the store has closed, so a closed
 * store's visitor never reaches the door (its 410 would surface as a console
 * error on every closed-store visit). The door's own 410 stays the backstop
 * for a store that closes between the two calls.
 */
async function mintForThisStore({ slug, axiosInstance, campaignId, templateAccountId }) {
  await refuseIfClosed({ slug, axiosInstance });
  return mintAnonymousStorefrontSession({
    axiosInstance,
    campaignId,
    templateAccountId,
  }).catch((err) => {
    // `/auth/getStoreToken` answers every refusal of the store with a uniform
    // 404; the template door 404s an unknown template. Both answer a store
    // that has CLOSED with 410 `store_closed`.
    if (isStoreClosed(err)) throw new StoreClosed();
    throw isStatus(err, 404) ? new StoreNotFound("token door") : err;
  });
}

/**
 * Throw `StoreClosed` when the backend says this store is closed. A failed
 * lookup (a 429 included) is ignored: before a mint the token door decides,
 * and answers 410 for a closed store anyway, so the pre-check can never block
 * an open store; for a store that cannot be opened, the visitor sees "Store
 * not found".
 */
async function refuseIfClosed({ slug, axiosInstance }) {
  let resp;
  try {
    resp = await axiosInstance.get(`/campaign/getIsStoreClosed/${slug}`, mintRequestConfig());
  } catch {
    return;
  }
  if (resp?.data?.closed) {
    throw new StoreClosed(closedNoticeFrom(resp.data));
  }
}

/**
 * Adopt the session another tab left in storage, if -- and only if -- it is
 * an anonymous session for this store. Resolves true when joined.
 */
async function joinStoredAnonymousSession({ store, campaignId, storedId }) {
  const storedToken = readStorage("token");
  if (!storedToken || !isAnonymousStorefrontSessionNow(storedToken)) {
    return false;
  }
  try {
    await adoptStorefrontAccount({
      store,
      accountId: storedId,
      requestConfig: mintRequestConfig(),
      expectCampaignId: campaignId,
    });
    return true;
  } catch (err) {
    if (err instanceof AccountNotForStore) return false;
    throw err;
  }
}

/**
 * Hold the stored anonymous bearer in memory (the browser can drop the
 * storage key mid-visit -- WebKit, prod 2026-10-01). Called only AFTER the
 * Vuex login: an in-memory session without a Vuex shopper account reads as a
 * live back-office session to App.vue for the length of the account
 * round-trip (gate bar-raiser P2-2). Anonymous only -- see
 * router/sessionProvenance.js.
 */
function holdAnonymousInMemory() {
  const token = readStorage("token");
  if (!token || !getActivePinia()) return;
  if (!isAnonymousStorefrontSessionNow(token)) return;
  useAccountStore().holdAnonymousStorefrontToken(token);
}
